import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Button,
    Card,
    Table,
    Space,
    Modal,
    Row,
    Col,
    Tooltip,
    Input,
    Form,
    Select,
    InputNumber,
    Descriptions,
    Typography,
    Checkbox,
    Tag,
    DatePicker,
} from 'antd';
import {
    PlusCircleOutlined,
    EditOutlined,
    ReloadOutlined,
    InfoCircleOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import { isEmpty } from 'underscore';
import dayjs from 'dayjs';

import { AppDispatch, CombinedStates } from '../../core/types';
import { SchoolExamResult, ExamResultFormData } from '../../core/exam/types';
import { getExamResults, saveExamResult, getExams, deleteExamResult } from '../../core/exam/examApi';
import './schoolExamsData.scss';

const { Option } = Select;
const { Text } = Typography;

// Helper function to format grade level display
const formatGradeLevel = (gradeLevel: number | undefined): string => {
    if (!gradeLevel) return '-';
    if (gradeLevel === 3) return 'III';
    if (gradeLevel === 4) return 'IV';
    return gradeLevel.toString();
};

const SchoolExamsData = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
    const [selectedResult, setSelectedResult] = useState<SchoolExamResult | null>(null);
    const [editingResult, setEditingResult] = useState<SchoolExamResult | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasParts, setHasParts] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());

    const { singleSchool } = useSelector((state: CombinedStates) => state.school);
    const { examResults, isFetching, isSaving, exams } = useSelector((state: CombinedStates) => state.exam);
    const { userData } = useSelector((state: CombinedStates) => state.user);

    // Check if user has roles 1, 2, 3, or 4
    const canSelectDate = userData?.role_id && [1, 2, 3, 4].includes(userData.role_id);

    useEffect(() => {
        // Fetch exams when component mounts
        if (isEmpty(exams)) {
            dispatch(getExams({}));
        }
    }, [dispatch]);

    useEffect(() => {
        if (singleSchool?.id) {
            const payload: { school_id: number; date?: string } = { school_id: singleSchool.id };

            // Add date parameter if user can select date
            if (canSelectDate) {
                payload.date = selectedDate.format('YYYY-MM-DD');
            }

            dispatch(getExamResults(payload));
        }
    }, [dispatch, singleSchool, canSelectDate, selectedDate]);

    const handleCreate = () => {
        form.validateFields()
            .then(values => {
                const formData: ExamResultFormData = {
                    ...values,
                    school_id: singleSchool?.id || 0,
                    has_parts: hasParts,
                    id: editingResult?.id || 0,
                };

                // Add date parameter if user can select date
                if (canSelectDate) {
                    formData.date = selectedDate.format('YYYY-MM-DD');
                }

                dispatch(saveExamResult(formData));
                setIsModalVisible(false);
                form.resetFields();
                setEditingResult(null);
                setHasParts(false);
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const handleEdit = (result: SchoolExamResult) => {
        setEditingResult(result);
        setHasParts(result.has_parts || false);
        form.setFieldsValue({
            exam_id: result.exam_id,
            period: result.period,
            students_taken_exam: result.students_taken_exam,
            students_passed_exam: result.students_passed_exam,
            students_scored_86_100: result.students_scored_86_100,
            hundreds_count: result.hundreds_count,
            students_3_or_more_passed: result.students_3_or_more_passed,
            average_score: result.average_score,
            total_exam_count_per_student: result.total_exam_count_per_student,
            grade_level: result.grade_level,
            exam_part: result.exam_part,
            evaluators_count: result.evaluators_count,
            is_exam_center: result.is_exam_center,
        });
        setIsModalVisible(true);
    };

    const handleShowInfo = (result: SchoolExamResult) => {
        setSelectedResult(result);
        setIsInfoModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setEditingResult(null);
        setHasParts(false);
    };

    const handleInfoModalCancel = () => {
        setIsInfoModalVisible(false);
        setSelectedResult(null);
    };

    const handleRefresh = () => {
        if (singleSchool?.id) {
            const payload: { school_id: number; date?: string } = { school_id: singleSchool.id };

            // Add date parameter if user can select date
            if (canSelectDate) {
                payload.date = selectedDate.format('YYYY-MM-DD');
            }

            dispatch(getExamResults(payload));
        }
    };

    const handleDelete = (resultId: number) => {
        Modal.confirm({
            title: 'Ar tikrai norite ištrinti šį egzamino rezultatą?',
            content: 'Šis veiksmas negali būti atšauktas.',
            okText: 'Ištrinti',
            okType: 'danger',
            cancelText: 'Atšaukti',
            onOk() {
                dispatch(deleteExamResult({ id: resultId }));
            },
        });
    };

    // Calculate filtered results dynamically based on current examResults and searchTerm
    const filteredResults = React.useMemo(() => examResults.filter(result => {
        if (!searchTerm) return true;
        const exam = exams.find(e => e.id === result.exam_id);
        return exam?.title.toLowerCase().includes(searchTerm.toLowerCase());
    }), [examResults, searchTerm, exams]);

    console.log('filteredResults', filteredResults);

    const columns = [
        {
            title: 'Egzaminas',
            dataIndex: 'exam_id',
            key: 'exam_id',
            render: (examId: number) => {
                const exam = exams.find(e => e.id === examId);
                return exam?.title || `Egzaminas ${examId}`;
            },
        },
        {
            title: 'Laikotarpis',
            dataIndex: 'period',
            key: 'period',
            render: (period: string) => period || '-',
        },
        {
            title: 'Klasė',
            dataIndex: 'grade_level',
            key: 'grade_level',
            render: (gradeLevel: number | undefined) => formatGradeLevel(gradeLevel),
        },
        {
            title: 'Egzamino dalis',
            dataIndex: 'exam_part',
            key: 'exam_part',
            render: (examPart: number) => {
                if (!examPart) return '-';
                if (examPart === 1) return 'Pirma dalis';
                return 'Antra dalis';
            },
        },
        {
            title: 'Laikė egzaminą',
            dataIndex: 'students_taken_exam',
            key: 'students_taken_exam',
            render: (value: number) => value || '-',
        },
        {
            title: 'Išlaikė',
            dataIndex: 'students_passed_exam',
            key: 'students_passed_exam',
            render: (value: number) => value || '-',
        },
        {
            title: 'Išlaikymo %',
            key: 'pass_rate',
            render: (_: unknown, record: SchoolExamResult) => {
                if (record.pass_rate !== undefined) {
                    return `${record.pass_rate}%`;
                }
                if (record.students_taken_exam && record.students_passed_exam !== undefined) {
                    const rate = ((record.students_passed_exam / record.students_taken_exam) * 100).toFixed(1);
                    return `${rate}%`;
                }
                return '-';
            },
        },
        {
            title: 'Vidurkis',
            dataIndex: 'average_score',
            key: 'average_score',
            render: (value: number) => (value ? value.toFixed(2) : '-'),
        },
        {
            title: 'Gavo aukščiausią įvertinimą',
            dataIndex: 'hundreds_count',
            key: 'hundreds_count',
            render: (value: number) => value || '-',
        },
        {
            title: 'Egzamino centras',
            dataIndex: 'is_exam_center',
            key: 'is_exam_center',
            render: (isExamCenter: boolean) => {
                if (isExamCenter === undefined || isExamCenter === null) return '-';
                const color = isExamCenter ? 'green' : 'red';
                const text = isExamCenter ? 'Taip' : 'Ne';
                return <Tag color={color}>{text}</Tag>;
            },
        },
        {
            title: 'Atnaujinta',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (text: string) => (text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-'),
        },
        {
            title: 'Veiksmai',
            key: 'actions',
            render: (_: unknown, record: SchoolExamResult) => (
                <Space>
                    <Tooltip title="Daugiau informacijos">
                        <Button
                            icon={<InfoCircleOutlined />}
                            onClick={() => handleShowInfo(record)}
                            size="small">
                            Info
                        </Button>
                    </Tooltip>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small">
                        Redaguoti
                    </Button>
                    <Button
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record?.id || 0)}
                        size="small"
                        danger>
                        Ištrinti
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className="school-exams-data-wrapper">
            <Card>
                <Row className="listing-table-title" justify="space-between" align="middle">
                    <Col className="listing-table-title-left">
                        <h2>Egzamino rezultatai</h2>
                        {singleSchool?.name && (
                            <p style={{ margin: 0, color: '#666' }}>
                                Mokykla: {singleSchool.name}
                            </p>
                        )}
                        <span style={{ fontWeight: 500 }}>
                            Iš viso: {filteredResults.length}
                        </span>
                    </Col>
                    <Col>
                        <Space>
                            {canSelectDate && (
                                <DatePicker
                                    value={selectedDate}
                                    onChange={(date) => { setSelectedDate(date || dayjs()); }}
                                    format="YYYY-MM-DD"
                                    placeholder="Pasirinkite datą"
                                    style={{ width: 150 }}
                                    allowClear={false}
                                    disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            )}
                            <Input
                                placeholder="Paieška pagal egzaminą..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ width: 250 }} />
                            <Tooltip title="Atnaujinti sąrašą">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefresh}
                                    loading={isFetching} />
                            </Tooltip>
                            <Button
                                loading={isSaving}
                                type="primary"
                                icon={<PlusCircleOutlined />}
                                onClick={() => {
                                    setEditingResult(null);
                                    form.resetFields();
                                    setIsModalVisible(true);
                                    setHasParts(false);
                                }}>
                                Pridėti rezultatą
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Table
                    dataSource={filteredResults}
                    columns={columns}
                    loading={isFetching || isSaving}
                    rowKey={(record) => `${record.id}-${record.school_id}-${record.exam_id}-${record.period}`}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} iš ${total} rezultatų`,
                    }} />
            </Card>

            {/* Edit/Create Modal */}
            <Modal
                title={editingResult ? 'Redaguoti egzamino rezultatą' : 'Pridėti naują egzamino rezultatą'}
                visible={isModalVisible}
                onOk={handleCreate}
                onCancel={handleCancel}
                okText={editingResult ? 'Atnaujinti' : 'Pridėti'}
                cancelText="Atšaukti"
                confirmLoading={isSaving}>
                <Form layout="vertical" form={form}>
                    <Form.Item
                        label="Egzaminas"
                        name="exam_id"
                        rules={[{ required: true, message: 'Pasirinkite egzaminą' }]}>
                        <Select placeholder="Pasirinkite egzaminą">
                            {exams.map(exam => (
                                <Option key={exam.id} value={exam.id}>
                                    {exam.title}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Klasė"
                                name="grade_level"
                                rules={[{ required: true, message: 'Pasirinkite klasę' }]}>
                                <Select placeholder="Pasirinkite klasę">
                                    <Option value={3}>III</Option>
                                    <Option value={4}>IV</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Vertintojų sk."
                                name="evaluators_count"
                                rules={[{ type: 'number', min: 0, message: 'Skaičius turi būti teigiamas' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                    min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Laikė egzaminą (mokinių skaičius)"
                                name="students_taken_exam"
                                rules={[{ required: true, message: 'Šis laukas yra privalomas' }, { type: 'number', min: 0, message: 'Skaičius turi būti teigiamas' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                    min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Išlaikė egzaminą (mokinių skaičius)"
                                name="students_passed_exam"
                                rules={[{ required: true, message: 'Šis laukas yra privalomas' }, { type: 'number', min: 0, message: 'Skaičius turi būti teigiamas' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                    min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Gavo 86-100 balų (mokinių skaičius)"
                                name="students_scored_86_100"
                                rules={[{ required: true, message: 'Šis laukas yra privalomas' }, { type: 'number', min: 0, message: 'Skaičius turi būti teigiamas' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                    min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Aukščiausi įvertinimai (100 / 7 TBD / Puikiai) mokinių skaičius"
                                name="hundreds_count"
                                rules={[{ required: true, message: 'Šis laukas yra privalomas' }, { type: 'number', min: 0, message: 'Skaičius turi būti teigiamas' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                    min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Vidutinis balas"
                                name="average_score"
                                rules={[{ required: true, message: 'Šis laukas yra privalomas' }, { type: 'number', min: 0, max: 100, message: 'Balas turi būti nuo 0 iki 100' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0.00"
                                    min={0}
                                    max={100}
                                    step={0.01}
                                    precision={2} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Vertintojų sk."
                                name="evaluators_count"
                                rules={[{ required: true, message: 'Šis laukas yra privalomas' }, { type: 'number', min: 0, message: 'Skaičius turi būti teigiamas' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                    min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Egzamino centras"
                                name="is_exam_center"
                                valuePropName="checked">
                                <Checkbox>
                                    Ar mokykla buvo egzamino centro vieta
                                </Checkbox>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Ar egzaminas turi dalis"
                                name="has_parts">
                                <Checkbox
                                    checked={hasParts}
                                    onChange={(e) => setHasParts(e.target.checked)}>
                                    Egzaminas turi dalis
                                </Checkbox>
                            </Form.Item>
                        </Col>
                        {hasParts && (
                            <Col span={12}>
                                <Form.Item
                                    label="Egzamino dalis"
                                    name="exam_part"
                                    rules={[{ required: true, message: 'Pasirinkite egzamino dalį' }]}>
                                    <Select placeholder="Pasirinkite egzamino dalį">
                                        <Option value={1}>Pirma dalis</Option>
                                        <Option value={2}>Antra dalis</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                    </Row>
                </Form>
            </Modal>

            {/* Info Modal */}
            <Modal
                title={(
                    <div>
                        <InfoCircleOutlined style={{ marginRight: 8, color: '#fdab01' }} />
                        Detali egzamino rezultatų informacija
                    </div>
                )}
                visible={isInfoModalVisible}
                onCancel={handleInfoModalCancel}
                footer={[
                    <Button key="close" onClick={handleInfoModalCancel}>
                        Uždaryti
                    </Button>,
                ]}
                width={800}>
                {selectedResult ? (
                    <div>
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Card size="small" title="Pagrindinė informacija" style={{ marginBottom: 16 }}>
                                    <Descriptions column={2} size="small">
                                        <Descriptions.Item label="Egzaminas">
                                            {exams.find(e => e.id === selectedResult.exam_id)?.title || `Egzaminas ${selectedResult.exam_id}`}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Laikotarpis">
                                            {selectedResult.period || '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Klasė">
                                            {formatGradeLevel(selectedResult.grade_level)}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Egzamino dalis">
                                            {(() => {
                                                if (!selectedResult.exam_part) return '-';
                                                return selectedResult.exam_part === 1 ? 'Pirma dalis' : 'Antra dalis';
                                            })()}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Laikė egzaminą">
                                            {selectedResult.students_taken_exam || 0} mokinių
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Išlaikė egzaminą">
                                            {selectedResult.students_passed_exam || 0} mokinių
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Vidutinis balas">
                                            {selectedResult.average_score ? selectedResult.average_score.toFixed(2) : '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Vertintojų skaičius">
                                            {selectedResult.evaluators_count || '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Egzamino centras">
                                            {(() => {
                                                if (selectedResult.is_exam_center === undefined || selectedResult.is_exam_center === null) {
                                                    return '-';
                                                }
                                                const color = selectedResult.is_exam_center ? 'green' : 'red';
                                                const text = selectedResult.is_exam_center ? 'Taip' : 'Ne';
                                                return <Tag color={color}>{text}</Tag>;
                                            })()}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Atnaujinta">
                                            {selectedResult.updated_at ? dayjs(selectedResult.updated_at).format('YYYY-MM-DD HH:mm') : '-'}
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Card size="small" title="Aukšti rezultatai" style={{ marginBottom: 16 }}>
                                    <Descriptions column={2} size="small">
                                        <Descriptions.Item label="Gavo 86-100 balų">
                                            {selectedResult.students_scored_86_100 || 0} mokinių
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Gavo 100 balų">
                                            {selectedResult.hundreds_count || 0} mokinių
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Card size="small" title="Apskaičiuoti rodikliai (%)" style={{ marginBottom: 16 }}>
                                    <Descriptions column={2} size="small">
                                        <Descriptions.Item label="Išlaikymo procentas">
                                            <Text strong>{selectedResult.pass_rate ? `${selectedResult.pass_rate}%` : '-'}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="86-100 balų gavusių dalis">
                                            <Text strong>{selectedResult.scored_86_100_rate ? `${selectedResult.scored_86_100_rate}%` : '-'}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="100 balų gavusių dalis">
                                            <Text strong>{selectedResult.hundreds_rate ? `${selectedResult.hundreds_rate}%` : '-'}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Aukščiausių įvertinimų dalis nuo visų mokinių">
                                            <Text strong>{selectedResult.top_scores_total_rate ? `${selectedResult.top_scores_total_rate}%` : '-'}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Egzaminą pasirinkusių dalis nuo visų mokinių">
                                            <Text strong>{selectedResult.exam_participation_rate ? `${selectedResult.exam_participation_rate}%` : '-'}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Išlaikiusių dalis nuo visų mokinių">
                                            <Text strong>{selectedResult.passed_total_rate ? `${selectedResult.passed_total_rate}%` : '-'}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="STEAM dalykų dalyvavimo dalis">
                                            <Text strong>{selectedResult.steam_participation_rate ? `${selectedResult.steam_participation_rate}%` : '-'}</Text>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                ) : null}
            </Modal>
        </div>
    );
};

export default SchoolExamsData;
